import { useEffect, useState } from 'react';
import { fetchPlanungsLage } from '../../services/planungsphaseService';
import { planungAmpel, type PlanungAmpel } from './planungAmpel';
import { weiterAusLage } from './planungKachel';

/**
 * Ampel-Stand der Planungsphase EINES Projekts fuer die Hub-Kachel
 * (Stufe 1.5/1.6). Liest `fetchPlanungsLage` und rechnet `planungAmpel` —
 * legt wie `usePlanung` NICHTS an: die Kachel zeigt den Stand, erzeugt aber
 * keinen Datensatz. Aus derselben Lage kommt der Weiter-Text des Leitfadens
 * (Stufe L3, `weiterAusLage`) — keine zweite Abfrage.
 *
 * Fail-open: scheitert das Laden, traegt der Stand `fehler` und keine Ampel.
 * Die Kachel zeigt dann „nicht lesbar" — nie gruen.
 *
 * Der Stand traegt den Schluessel, fuer den er gilt (Muster
 * useModuleActivity): beim Projektwechsel zeigt die Kachel nie den Stand des
 * vorigen Projekts, ohne dass der Effekt synchron zuruecksetzen muesste.
 */
export interface PlanungKachelEingang {
    heuteIso: string;
    baustartIso: string | null;
    lvHatPlanungsleistung: boolean;
}

export interface PlanungKachelStand {
    laden: boolean;
    /** Gibt es die Phase schon? Steuert den Hinweis 1.6 auf der Kachel. */
    phaseVorhanden: boolean;
    ampel: PlanungAmpel | null;
    /** „Weiter: …" des Leitfadens (Entscheid E-L8) aus derselben Lage; leer
     *  ohne Phase, bei Lesefehler und wenn nichts mehr zu tun ist. */
    weiter: string;
    /** Lesefehler im Klartext — dann ist `ampel` null. */
    fehler: string | null;
}

const LADEND: PlanungKachelStand = { laden: true, phaseVorhanden: false, ampel: null, weiter: '', fehler: null };
const INAKTIV: PlanungKachelStand = { laden: false, phaseVorhanden: false, ampel: null, weiter: '', fehler: null };

export const usePlanungKachel = (
    projectId: string | null | undefined,
    eingang: PlanungKachelEingang,
    aktiv: boolean = true,
): PlanungKachelStand => {
    const { heuteIso, baustartIso, lvHatPlanungsleistung } = eingang;
    const gueltig = aktiv && !!projectId && projectId !== 'temp-id';
    const schluessel = gueltig ? `${projectId}|${heuteIso}|${baustartIso ?? ''}|${lvHatPlanungsleistung ? 1 : 0}` : '';
    const [stand, setStand] = useState<{ fuer: string } & PlanungKachelStand>({ fuer: '', ...LADEND });

    useEffect(() => {
        if (!schluessel || !projectId) return;
        let abgebrochen = false;
        fetchPlanungsLage(projectId, { streng: true })
            .then(lage => {
                if (abgebrochen) return;
                const ampel = planungAmpel({
                    phase: lage.phase,
                    fragen: lage.fragen,
                    hinweise: lage.hinweise,
                    heuteIso,
                    baustartIso,
                    lvHatPlanungsleistung,
                });
                // „Rest bleibt offen" steht am Kopfsatz und kommt mit
                // `lage.phase` — dieselbe naechste Station wie im Modul und
                // in der Sammelabfrage, auf jedem Geraet.
                const weiter = weiterAusLage({
                    phase: lage.phase,
                    fragen: lage.fragen,
                    hinweise: lage.hinweise,
                    heuteIso,
                    baustartIso,
                });
                setStand({ fuer: schluessel, laden: false, phaseVorhanden: !!lage.phase, ampel, weiter, fehler: null });
            })
            .catch((e: unknown) => {
                if (abgebrochen) return;
                console.warn('Planungsstand der Kachel konnte nicht geladen werden.', e);
                setStand({
                    fuer: schluessel,
                    laden: false,
                    phaseVorhanden: false,
                    ampel: null,
                    weiter: '',
                    fehler: e instanceof Error ? e.message : String(e),
                });
            });
        return () => { abgebrochen = true; };
    }, [schluessel, projectId, heuteIso, baustartIso, lvHatPlanungsleistung]);

    if (!schluessel) return INAKTIV;
    return stand.fuer === schluessel ? stand : LADEND;
};
